import { FieldArray, FormikHelpers, FormikValues } from 'formik';
import React from 'react';
import { array, mixed, object, string } from 'yup';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Form, {
  Botao as BotaoForm,
  CaixaDeSelecao,
  CaixaDeTexto,
  Campo,
  Contexto,
  DropZone,
} from 'Component/Form';
import Icone from 'Component/Icone';
import Modal from 'Component/Modal';
import Alternativa from 'Interface/Alternativas';
import Questao from 'Interface/Questoes';
import { RetornoQuiz } from 'Interface/Quizzes';
import { Erro } from 'Interface/Requisicao';
import { IMAGENS } from 'View/constants';
import { useAdicionarQuizMutation } from 'View/Service/Default/Quizzes';
import { useAdicionarQuizAlternativaMutation } from 'View/Service/Default/QuizzesAlternativas';

interface Props {
  exibir: boolean;
  unidadeDeAprendizagemId: number;
  aoFechar: (estado: boolean) => void;
}

const regras = object({
  enunciado: string()
    .nullable()
    .test('correta', 'Selecione a resposta correta', (valor, contexto) => {
      let selecionou = false;

      contexto.parent.alternativas?.map((alternativa: Alternativa) =>
        alternativa.correta ? (selecionou = true) : null
      );

      return selecionou;
    })
    .test(
      'correta',
      'Existe mais de uma resposta correta',
      (valor, contexto) => {
        let corretas = 0;

        contexto.parent.alternativas?.map((alternativa: Alternativa) =>
          alternativa.correta ? corretas++ : null
        );

        return corretas <= 1;
      }
    )
    .test(
      'semnumeral',
      'Não deve conter nenhum prefixo como: 1., 2 -, A -...',
      (valor) => {
        if (
          valor?.startsWith('1') ||
          valor?.startsWith('2') ||
          valor?.startsWith('3') ||
          valor?.startsWith('4') ||
          valor?.startsWith('5') ||
          valor?.startsWith('.') ||
          valor?.startsWith('-')
        ) {
          return false;
        }

        return true;
      }
    )
    .required('Obrigatório'),
  imagem: mixed()
    .test('tamanho', 'O arquivo é muito grande', (valor) => {
      if (valor && valor.size) {
        return valor.size <= 500000;
      }
      return true;
    })
    .test('tipo', 'Formato não suportado', (valor) => {
      if (valor && valor.type) {
        return IMAGENS.includes(valor.type);
      }
      return true;
    })
    .optional(),
  alternativas: array().of(
    object().shape({
      texto: string().required('Obrigatório'),
    })
  ),
});

const Adicionar = ({
  exibir,
  aoFechar,
  unidadeDeAprendizagemId,
}: Props): React.ReactElement => {
  const toast = useToast();
  const [adicionarQuiz] = useAdicionarQuizMutation();
  const [adicionarAlternativa] = useAdicionarQuizAlternativaMutation();

  const adicinar = async (
    dados: Questao,
    form: FormikHelpers<FormikValues>
  ): Promise<void> => {
    try {
      const { alternativas, ...resto } = dados;

      const { quiz } = (await adicionarQuiz({
        ...resto,
        unidade_de_aprendizagem_id: unidadeDeAprendizagemId,
      }).unwrap()) as unknown as RetornoQuiz;

      if (alternativas) {
        await Promise.all(
          alternativas?.map((alternativa) =>
            adicionarAlternativa({ ...alternativa, quiz_id: quiz.id })
          )
        );
      }

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });

      aoFechar(false);
    } catch (erro) {
      const { data: errors } = erro as Erro;

      form.setStatus(errors);

      toast({
        titulo: 'Erro ao adicionar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <Modal lg exibir={exibir} aoFechar={aoFechar}>
      <Form onSubmit={adicinar} regras={regras}>
        <div className="row">
          <div className="col-md-6">
            <CaixaDeTexto
              name="enunciado"
              rotulo="Enunciado do quiz"
              altura={288}
            />
          </div>
          <div className="col-md-6">
            <DropZone
              name="imagem"
              ajuda="Caso tenha uma imagem, faça o upload aqui."
              path="unidade-de-aprendizagem/quiz"
            />
          </div>
        </div>
        <FieldArray name="alternativas">
          {({ push, remove }) => (
            <React.Fragment>
              <Contexto>
                {({ values }) => (
                  <React.Fragment>
                    {values.alternativas &&
                      values.alternativas?.map(
                        (alternativa: Alternativa, indice: number) => (
                          <div
                            key={indice.toString()}
                            className="hstack gap-3 mb-3"
                          >
                            <CaixaDeSelecao
                              name={`alternativas.${indice}.correta`}
                              semMargem
                            />
                            <Campo
                              name={`alternativas.${indice}.texto`}
                              rotulo={`Alternativa #${indice + 1}`}
                              semMargem
                            />
                            <Botao
                              icone="trash"
                              tema="danger"
                              onClick={() => remove(indice)}
                            />
                          </div>
                        )
                      )}
                    {!values.alternativas && (
                      <div className="mt-5 px-3">
                        <Icone
                          nome="patch-question-fill"
                          className="display-1 d-block text-center text-primary"
                        />
                        <p className="lead text-center mb-0">
                          Nenhuma alternativa adicionada
                        </p>
                        <small className="d-block text-muted text-center mb-5">
                          Você pode clicar no botão abaixo para adicionar
                          alternativas a esse quiz.
                        </small>
                      </div>
                    )}
                  </React.Fragment>
                )}
              </Contexto>
              <div className="d-flex justify-content-center">
                <Botao
                  icone="plus-lg"
                  tema="success"
                  onClick={() => push({ texto: '' })}
                >
                  Alternativa
                </Botao>
              </div>
            </React.Fragment>
          )}
        </FieldArray>
        <div className="d-flex justify-content-end gap-2 mt-3">
          <BotaoForm>Salvar</BotaoForm>
          <Botao onClick={() => aoFechar(false)} tema="light">
            Cancelar
          </Botao>
        </div>
      </Form>
    </Modal>
  );
};

export default Adicionar;
